import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../../services/api-client';
import { Badge } from '../../components/ui/badge';

type WatchManifest = {
  candidate_display_name: string;
  is_ready: boolean;
  processing_status: string;
  video_url?: string | null;
  poster_url?: string | null;
};

type WatchEventType = 'page_view' | 'video_start' | 'video_complete';

const publicApiUrl = (path: string) => `${API_URL}${path}`;

const getWatchSessionId = (watchToken: string) => {
  const fallback = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  const key = `candidate_video_watch_session_${watchToken}`;
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) {
      return existing;
    }
    const next = window.crypto?.randomUUID?.() || fallback();
    window.localStorage.setItem(key, next);
    return next;
  } catch {
    return fallback();
  }
};

export default function WatchPage() {
  const { watchToken } = useParams();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [manifest, setManifest] = useState<WatchManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!watchToken) {
      setError('This video link is unavailable.');
      return;
    }
    sessionIdRef.current = getWatchSessionId(watchToken);
    fetch(publicApiUrl(`/watch/${watchToken}/manifest`))
      .then(async response => {
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.detail || 'This video link is unavailable.');
        }
        return response.json();
      })
      .then((data: WatchManifest) => {
        setManifest(data);
        recordEvent('page_view');
      })
      .catch(err => setError(err?.message || 'This video link is unavailable.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchToken]);

  const recordEvent = (event_type: WatchEventType) => {
    if (!watchToken) {
      return;
    }
    fetch(publicApiUrl(`/watch/${watchToken}/event`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type,
        watch_session_id: sessionIdRef.current || getWatchSessionId(watchToken),
      }),
    }).catch(() => undefined);
  };

  if (error) {
    return <main className="min-h-screen grid place-items-center bg-gray-50 p-6 text-charcoal">{error}</main>;
  }

  if (!manifest) {
    return <main className="min-h-screen grid place-items-center bg-gray-50 text-charcoal">Loading...</main>;
  }

  return (
    <main className="min-h-screen bg-gray-50 text-charcoal">
      <div className="mx-auto flex min-h-screen max-w-4xl flex-col justify-center px-6 py-10">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">Thank-you video</p>
            <h1 className="font-title text-3xl text-charcoal">{manifest.candidate_display_name}</h1>
          </div>
          <Badge variant={manifest.is_ready ? 'success' : 'warning'}>{manifest.processing_status}</Badge>
        </div>
        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          {manifest.is_ready && manifest.video_url ? (
            <video
              ref={videoRef}
              src={manifest.video_url}
              poster={manifest.poster_url || undefined}
              className="aspect-video w-full bg-black"
              controls
              playsInline
              onPlay={() => recordEvent('video_start')}
              onEnded={() => recordEvent('video_complete')}
            />
          ) : (
            <div className="grid aspect-video place-items-center p-6 text-gray-600">
              The video is being prepared.
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
